/**
 * Where /app sends a visitor who does not have the app, and the URL that opens
 * it for the visitor who does.
 *
 * The app claims /app on this host in
 * public/.well-known/apple-app-site-association, but iOS will not hand a
 * Universal Link to the app when the tap happened on the host serving it, so
 * that claim does nothing for a button on this site. The custom scheme carries
 * no such rule and is what actually opens the app from here — see OpenInApp.
 *
 * The store default is a placeholder with a fake id. OpenApp.hasStoreListing
 * rejects it rather than redirecting to a dead listing, and
 * scripts/prelaunch-check.js fails a build whose REACT_APP_APP_STORE_URL is
 * set to something that is not a listing.
 */

export const APP_STORE_URL =
  process.env.REACT_APP_APP_STORE_URL ||
  "https://apps.apple.com/app/id0000000000";

/**
 * `<scheme>://app` for this environment, or "" when no scheme is configured.
 *
 * There is deliberately no default. Production, UAT and dev each register their
 * own scheme, and the three builds sit on the same tester device: iOS gives a
 * `<scheme>://` URL to whichever installed app claims it, with no defined
 * winner when two claim the same one. A baked-in "gradusai" would mean the UAT
 * site opening the production app against production data, so unset means "do
 * not try" — the same honest state an unset store URL already has.
 *
 * The `app` host is built in rather than configured. The app's router matches
 * on the path and discards the host, so the fixed `app` absorbs that drop and
 * everything after it becomes the route; gradusai://results/123 would arrive as
 * the path /123 and open the wrong screen. See
 * docs/api-changes/2026-08-27-app-url-schemes.md §2.
 */
export const APP_SCHEME_URL = process.env.REACT_APP_APP_SCHEME
  ? `${process.env.REACT_APP_APP_SCHEME}://app`
  : "";

// ponytail: iOS only this release. Android needs PLAY_STORE_URL back here, the
// Android branch in storeUrlFor, a Play button on /app, and
// public/.well-known/assetlinks.json restored (git history has the file). It
// registers none of these schemes either — the app has no VIEW/BROWSABLE
// intent-filter yet.
